// src/pages/clubs/ClubSettings.jsx
// Edit club name, contact info, logo. Danger zone: leave/delete club.
// Route: /clubs/:clubId/settings

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc, deleteDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import useClub from '@/hooks/useClub';
import { uploadBlobToStorage } from '@/services/storageService';
import SquareLogoUploader from '@/components/inputs/SquareLogoUploader.jsx';
import { Avatar } from '@/components/avatar';
import PageSpinner from '@/components/PageSpinner';
import toast from 'react-hot-toast';
import clsx from 'clsx';

import {
  BuildingOffice2Icon,
  ChevronLeftIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  TrashIcon,
  InformationCircleIcon,
  TrophyIcon,
  PlusIcon,
  CheckCircleIcon,
  CreditCardIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { findAttachableTeamsForUser, attachTeamsToClub } from '@/services/clubService';
import TransferOwnershipModal from '@/components/clubs/TransferOwnershipModal';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ------------------------------------------------------------------ */
/* Field component                                                      */
/* ------------------------------------------------------------------ */
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#0d1f14] mb-1">
        {label}
      </label>
      {hint && <p className="text-xs text-zinc-500 mb-2">{hint}</p>}
      {children}
    </div>
  );
}

const inputCls = 'w-full rounded-xl border border-zinc-300 bg-white px-3.5 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 transition';

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */
export default function ClubSettings() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { firebaseUser } = useAuth();
  const { club, members, isAdmin, isOwner, loading } = useClub(clubId);
  const [transferOpen, setTransferOpen] = useState(false);

  // Stripe Connect state
  const [connectingStripe,  setConnectingStripe]  = useState(false);
  const [dashboardLinking,  setDashboardLinking]  = useState(false);

  const [name,           setName]           = useState('');
  const [sport,          setSport]          = useState('');
  const [website,        setWebsite]        = useState('');
  const [email,          setEmail]          = useState('');
  const [phone,          setPhone]          = useState('');
  const [allowTeamLogos, setAllowTeamLogos] = useState(true);
  const [saving,         setSaving]         = useState(false);

  const SPORTS = ['Soccer', 'Basketball', 'Baseball', 'Softball', 'Football', 'Volleyball', 'Lacrosse', 'Hockey', 'Other'];

  const [processedLogo, setProcessedLogo] = useState(null);
  const [logoPreview,   setLogoPreview]   = useState(null);
  const [savingLogo,    setSavingLogo]    = useState(false);

  const [confirmDelete,  setConfirmDelete]  = useState('');
  const [deleting,       setDeleting]       = useState(false);
  const [propagating,    setPropagating]    = useState(false);

  // Attach-existing-teams state. Populated by findAttachableTeamsForUser
  // — covers teams the founder owns/coaches that aren't in this club yet.
  // The forward-create path in CreateTeams.jsx already syncs both sides
  // for new teams; this surface fixes legacy teams created before the club.
  const [attachable,     setAttachable]     = useState(null); // null = loading
  const [attaching,      setAttaching]      = useState(new Set());
  const reloadAttachable = async () => {
    if (!firebaseUser?.uid || !clubId) return;
    try {
      const list = await findAttachableTeamsForUser({ uid: firebaseUser.uid, clubId });
      setAttachable(list);
    } catch (err) {
      console.warn('[ClubSettings] attachable teams lookup failed', err);
      setAttachable([]);
    }
  };
  useEffect(() => { reloadAttachable(); /* eslint-disable-next-line */ }, [firebaseUser?.uid, clubId]);

  const handleAttach = async (teamId) => {
    setAttaching((prev) => new Set(prev).add(teamId));
    try {
      const { attached, skipped } = await attachTeamsToClub({ clubId, teamIds: [teamId] });
      if (attached.length) {
        toast.success('Team attached to club');
      } else if (skipped.length) {
        toast.error('Team is already in another club');
      }
      await reloadAttachable();
    } catch (err) {
      console.error('[ClubSettings] attach failed', err);
      toast.error(err?.message || 'Could not attach team');
    } finally {
      setAttaching((prev) => { const n = new Set(prev); n.delete(teamId); return n; });
    }
  };

  // Seed form when club loads
  useEffect(() => {
    if (!club) return;
    setName(club.name || '');
    setSport(club.sport || '');
    setWebsite(club.website || '');
    setEmail(club.contactEmail || '');
    setPhone(club.phone || '');
    setLogoPreview(club.logoUrl || null);
    setAllowTeamLogos(club.allowTeamLogos !== false);
  }, [club?.id]);

  // Handle Stripe Connect return URLs (?stripe=success|refresh)
  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'success') {
      toast.success('Stripe connected! Payments will activate within a few minutes.');
      setSearchParams({}, { replace: true });
    } else if (stripeParam === 'refresh') {
      toast('Stripe setup timed out — click Connect again to resume.', { icon: '⚠️' });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Connect Stripe — creates Express account + returns onboarding URL
  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const fn = httpsCallable(getFunctions(), 'createConnectAccount');
      const { data } = await fn({ clubId });
      window.location.href = data.url; // redirect to Stripe-hosted onboarding
    } catch (err) {
      console.error('[ClubSettings] createConnectAccount failed', err);
      toast.error(err?.message || 'Could not start Stripe setup. Try again.');
      setConnectingStripe(false);
    }
  };

  // Stripe Express Dashboard link — for already-connected clubs
  const handleStripeDashboard = async () => {
    setDashboardLinking(true);
    try {
      const fn = httpsCallable(getFunctions(), 'getConnectDashboardLink');
      const { data } = await fn({ clubId });
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('[ClubSettings] getConnectDashboardLink failed', err);
      toast.error(err?.message || 'Could not open Stripe dashboard.');
    } finally {
      setDashboardLinking(false);
    }
  };

  if (loading) return <PageSpinner />;

  if (!club || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#f7faf5] flex flex-col items-center justify-center text-center px-4">
        <ExclamationTriangleIcon className="h-12 w-12 text-zinc-300 mb-4" />
        <h2 className="text-lg font-semibold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>Access denied</h2>
        <p className="text-sm text-zinc-500 mt-1 mb-4">Only club admins can edit settings.</p>
        <Link to={`/clubs/${clubId}`} className="text-sm text-[#5a8a1f] hover:underline">← Back to Club</Link>
      </div>
    );
  }

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Club name is required.'); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'clubs', clubId), {
        name:           name.trim(),
        sport:          sport || null,
        website:        website.trim(),
        contactEmail:   email.trim(),
        phone:          phone.trim(),
        allowTeamLogos: allowTeamLogos,
        updatedAt:      serverTimestamp(),
      });
      toast.success('Club settings saved.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLogo = async () => {
    if (!processedLogo?.blob) return;
    // Auth-required guard. The previous version fell back to 'anon' here
    // which silently created orphan uploads under `clubLogos/anon_{clubId}/`
    // if the user lost auth mid-upload (token expiry / mid-flight signOut).
    // Better: refuse the upload outright so the parent flow (handleSaveLogo's
    // setSavingLogo state) doesn't get stuck and we don't pollute Storage.
    const uid = firebaseUser?.uid;
    if (!uid) {
      toast.error('Please sign in again to upload your logo.');
      return;
    }
    setSavingLogo(true);
    try {
      const path = `clubLogos/${uid}_${clubId}/logo_${Date.now()}.webp`;
      const { downloadURL } = await uploadBlobToStorage(processedLogo.blob, path, {
        contentType: processedLogo.blob.type,
      });
      await updateDoc(doc(db, 'clubs', clubId), {
        logoUrl:   downloadURL,
        updatedAt: serverTimestamp(),
      });
      setLogoPreview(downloadURL);
      setProcessedLogo(null);
      toast.success('Club logo updated.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload logo.');
    } finally {
      setSavingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    // Single-click logo removal also strips the inherited logo from any
    // team that's still using it (`clubLogoUrl` is read at team-render
    // time). Recoverable via re-upload, but worth a beat to confirm —
    // accidental clicks here regenerate the "why did my logo disappear"
    // support thread.
    const ok = typeof window !== 'undefined' && window.confirm(
      'Remove the club logo? Teams that inherit it will fall back to their initial.'
    );
    if (!ok) return;
    try {
      await updateDoc(doc(db, 'clubs', clubId), {
        logoUrl:   null,
        updatedAt: serverTimestamp(),
      });
      setLogoPreview(null);
      setProcessedLogo(null);
      toast.success('Logo removed.');
    } catch (err) {
      toast.error('Failed to remove logo.');
    }
  };

  const handleDelete = async () => {
    if (confirmDelete !== club.name) {
      toast.error(`Type the club name exactly to confirm.`);
      return;
    }
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'clubs', clubId));
      toast.success('Club deleted.');
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete club.');
    } finally {
      setDeleting(false);
    }
  };

  const handlePropagate = async () => {
    const teamIds = club?.teamIds;
    if (!teamIds?.length) return;

    // Destructive bulk write — overwrites the sport + logo permission on
    // every team in the club. The static amber callout above the button
    // warns about it, but a one-click commit is too easy to misfire from
    // the keyboard. Force a deliberate confirm with the team count + a
    // summary of what changes.
    const summary = [
      sport ? `Sport will be locked to "${sport}"` : 'Sport lock will be removed',
      allowTeamLogos ? 'Team-specific logos allowed' : 'All teams will display the club logo',
    ].join('\n  • ');
    const ok = typeof window !== 'undefined' && window.confirm(
      `Apply current club settings to ${teamIds.length} team${teamIds.length === 1 ? '' : 's'}?\n\n` +
      `  • ${summary}\n\n` +
      `This overwrites each team's existing sport and logo settings. Continue?`
    );
    if (!ok) return;

    setPropagating(true);
    try {
      const batch = writeBatch(db);
      const currentLogoUrl = logoPreview || club.logoUrl || null;
      const lockLogo = !allowTeamLogos;
      teamIds.forEach((teamId) => {
        const teamRef = doc(db, 'teams', teamId);
        const updates = {
          sportLockedByClub: !!sport,
          logoLockedByClub:  lockLogo,
          clubLogoUrl:       currentLogoUrl,
          updatedAt:         serverTimestamp(),
        };
        if (sport) {
          updates.sport = sport;
        }
        // When the lock is on, team-specific logos are NOT allowed — every
        // renderer in the app reads `team.logoUrl`, so we must overwrite
        // it (not just the `clubLogoUrl` fallback) for the change to be
        // visible everywhere (sidebar, team cards, registration pages,
        // dashboards, etc.). When the lock is off, leave each team's own
        // logo intact and only refresh the fallback.
        if (lockLogo && currentLogoUrl) {
          updates.logoUrl = currentLogoUrl;
        }
        batch.update(teamRef, updates);
      });
      await batch.commit();
      toast.success(`Settings applied to ${teamIds.length} team${teamIds.length === 1 ? '' : 's'}.`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to propagate settings to teams.');
    } finally {
      setPropagating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">

        {/* ── Header ─────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <Link
            to={`/clubs/${clubId}`}
            className="p-2 rounded-xl text-zinc-400 hover:text-[#5a8a1f] hover:bg-[#b5f03d]/10 transition-colors"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-3">
            {logoPreview ? (
              <img src={logoPreview} alt="Club logo" className="h-12 w-12 rounded-xl object-contain border border-zinc-200 bg-white p-0.5" />
            ) : (
              <div className="h-12 w-12 rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center">
                <BuildingOffice2Icon className="h-6 w-6 text-[#5a8a1f]" />
              </div>
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>Club</p>
              <h1 className="text-2xl font-bold uppercase tracking-tight text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>Settings</h1>
              <p className="text-xs text-zinc-500">{club.name}</p>
            </div>
          </div>
        </div>

        {/* ── Club Logo ──────────────────────────────── */}
        <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-6 space-y-4">
          <h2 className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>Club Logo</h2>
          <p className="text-xs text-zinc-500 flex items-start gap-1.5">
            <InformationCircleIcon className="h-4 w-4 shrink-0 mt-0.5" />
            When coaches create new teams under this club, they can inherit this logo automatically.
          </p>

          <div className="flex items-center gap-4">
            <Avatar
              src={logoPreview}
              alt="Club logo"
              square
              className="!h-16 !w-16 flex-shrink-0 border border-zinc-200 p-0.5 bg-zinc-50"
              initials={club.name?.charAt(0)?.toUpperCase() || 'C'}
            />
            <div className="flex-1 space-y-2">
              <SquareLogoUploader
                label="Choose new logo"
                initialPreview={null}
                maxSize={512}
                mimeType="image/webp"
                quality={0.9}
                showPreview={false}
                bordered
                caption={null}
                onProcessed={(res) => {
                  setProcessedLogo(res);
                  setLogoPreview(res.dataUrl);
                }}
              />
              <div className="flex items-center gap-3">
                {processedLogo && (
                  <button
                    type="button"
                    onClick={handleSaveLogo}
                    disabled={savingLogo}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-1.5 text-xs font-bold uppercase disabled:opacity-60 transition-all active:scale-[0.97]"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    {savingLogo ? <><ArrowPathIcon className="h-3 w-3 animate-spin" />Saving…</> : 'Save Logo'}
                  </button>
                )}
                {logoPreview && !processedLogo && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="h-3 w-3" /> Remove logo
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Payments (Stripe Connect) ──────────────────
            Owner-only card. Shows Connect CTA when not yet connected,
            confirmed state + dashboard link when connected. */}
        {isOwner && (
          <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className={clsx(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1',
                club.stripeConnected
                  ? 'bg-[#b5f03d]/15 ring-[#b5f03d]/40'
                  : 'bg-zinc-100 ring-zinc-200',
              )}>
                <CreditCardIcon className={clsx('h-5 w-5', club.stripeConnected ? 'text-[#5a8a1f]' : 'text-zinc-400')} />
              </div>
              <div className="flex-1 min-w-0">
                <h2
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  Payments
                </h2>
                {club.stripeConnected ? (
                  <>
                    <p className="text-sm text-zinc-700 mt-1">
                      Your Stripe account is connected. Registration payments go directly to your bank account.
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#b5f03d]/15 border border-[#b5f03d]/40 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#3d6010]" style={{ fontFamily: HEAD_FONT }}>
                        <CheckCircleIcon className="h-3 w-3" /> Connected
                      </span>
                      <button
                        type="button"
                        onClick={handleStripeDashboard}
                        disabled={dashboardLinking}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50 px-3 py-1.5 text-xs font-bold uppercase text-zinc-700 transition-all disabled:opacity-50"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        {dashboardLinking
                          ? <><ArrowPathIcon className="h-3 w-3 animate-spin" /> Opening…</>
                          : <><ArrowTopRightOnSquareIcon className="h-3 w-3" /> Manage payouts</>
                        }
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-zinc-700 mt-1">
                      Connect a Stripe account to collect registration payments directly into your bank account.
                    </p>
                    <button
                      type="button"
                      onClick={handleConnectStripe}
                      disabled={connectingStripe}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] active:scale-[0.97] text-[#0d1f14] px-4 py-2 text-sm font-bold uppercase transition-all disabled:opacity-50 shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                    >
                      {connectingStripe
                        ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Setting up…</>
                        : <><CreditCardIcon className="h-4 w-4" /> Connect Stripe</>
                      }
                    </button>
                    {club.stripeAccountId && !club.stripeConnected && (
                      <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
                        Setup in progress — click Connect Stripe to resume.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Attach existing teams ──────────────────────
            Promoted to the TOP of settings (above General Info) so a
            club admin who arrives here from the empty-state nudge in
            ClubBroadcast / ClubDashboard sees the action immediately
            without having to scroll past the whole settings form.
            Self-hides when there's nothing to attach so a clean club
            doesn't get noise. New teams created from inside the club
            already auto-link via CreateTeams.jsx — this surface only
            handles the legacy/discovery case. */}
        {isAdmin && attachable !== null && (() => {
          const unattached = attachable.filter((t) => !t.clubId);
          const inOther    = attachable.filter((t) => t.alreadyElsewhere);
          if (!unattached.length && !inOther.length) return null;
          // Lime border + faint lime tint when there are unattached
          // teams — visually screams "actionable callout" so it's
          // unmissable. Dims to plain zinc when only inOther rows
          // remain (informational, no work to do).
          const hasWork = unattached.length > 0;
          return (
            <div
              className={
                'rounded-2xl border p-6 space-y-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] ' +
                (hasWork
                  ? 'border-[#b5f03d]/50 bg-[#b5f03d]/[0.06]'
                  : 'border-zinc-200 bg-white')
              }
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                    <PlusIcon className="h-5 w-5 text-[#5a8a1f]" />
                  </div>
                  <div>
                    <h2
                      className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      {hasWork
                        ? `Attach existing teams (${unattached.length})`
                        : 'Attach existing teams'}
                    </h2>
                    <p className="text-sm text-zinc-700 mt-1">
                      {hasWork
                        ? 'Teams you manage that aren\'t linked to this club yet. Attaching makes them show up in club broadcasts and reports.'
                        : 'Teams you manage that are already in another club.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Unattached teams — one-tap attach */}
              {unattached.length > 0 && (
                <ul className="space-y-2">
                  {unattached.map((t) => {
                    const isAttaching = attaching.has(t.id);
                    return (
                      <li
                        key={t.id}
                        className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3 hover:border-[#b5f03d]/40 transition-colors"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                          {t.logoUrl ? (
                            <img src={t.logoUrl} alt="" className="h-9 w-9 rounded-lg object-cover" />
                          ) : (
                            <TrophyIcon className="h-4 w-4 text-[#5a8a1f]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#0d1f14] truncate">{t.name}</p>
                          <p className="text-xs text-zinc-500 truncate">
                            {[t.sport, t.ageGroup, t.gender].filter(Boolean).join(' · ') || 'Team'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAttach(t.id)}
                          disabled={isAttaching}
                          className="inline-flex items-center gap-1.5 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-1.5 text-xs font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-60"
                          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                        >
                          {isAttaching ? (
                            <>
                              <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                              Attaching…
                            </>
                          ) : (
                            <>
                              <PlusIcon className="h-3.5 w-3.5" />
                              Attach
                            </>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Teams already in another club — show but don't allow click */}
              {inOther.length > 0 && (
                <div className={'pt-3 ' + (unattached.length > 0 ? 'border-t border-zinc-100' : '')}>
                  <p
                    className="text-[10px] font-bold uppercase tracking-[1.2px] text-zinc-400 mb-2"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    In another club
                  </p>
                  <ul className="space-y-2">
                    {inOther.map((t) => (
                      <li key={t.id} className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-2.5 opacity-70">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-200">
                          <TrophyIcon className="h-4 w-4 text-zinc-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-zinc-700 truncate">{t.name}</p>
                          <p className="text-xs text-zinc-400 truncate">
                            Already in {t.clubName || 'another club'}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── General Info ───────────────────────────── */}
        <form onSubmit={handleSave} className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-6 space-y-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>General Information</h2>

          <Field label="Club Name" hint="The official name of your club or organization.">
            <input
              type="text"
              className={inputCls}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Eastside United FC"
              required
            />
          </Field>

          <Field label="Primary Sport" hint="If set, all teams under this club inherit this sport and cannot change it.">
            <select
              className={inputCls}
              value={sport}
              onChange={e => setSport(e.target.value)}
            >
              <option value="">Multi-sport / not specified</option>
              {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>

          <Field label="Website" hint="Optional. Include https://">
            <input
              type="url"
              className={inputCls}
              value={website}
              onChange={e => setWebsite(e.target.value)}
              placeholder="https://yourclub.com"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Contact Email">
              <input
                type="email"
                className={inputCls}
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="info@yourclub.com"
              />
            </Field>
            <Field label="Phone">
              <input
                type="tel"
                className={inputCls}
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+1 (555) 000-0000"
              />
            </Field>
          </div>

          {/* ── Team Permissions ── */}
          <div className="border-t border-zinc-100 pt-5">
            <h3 className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3" style={{ fontFamily: HEAD_FONT }}>Team Permissions</h3>

            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative mt-0.5 shrink-0">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={allowTeamLogos}
                  onChange={e => setAllowTeamLogos(e.target.checked)}
                />
                <div
                  onClick={() => setAllowTeamLogos(v => !v)}
                  className={clsx(
                    'h-5 w-9 rounded-full transition-colors cursor-pointer',
                    allowTeamLogos ? 'bg-[#b5f03d]' : 'bg-zinc-300'
                  )}
                >
                  <div className={clsx(
                    'mt-0.5 ml-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                    allowTeamLogos ? 'translate-x-4' : 'translate-x-0'
                  )} />
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0d1f14] group-hover:text-[#3d6010]">
                  Allow teams to use custom logos
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {allowTeamLogos
                    ? 'Coaches can upload a team-specific logo. It overrides the club logo for that team.'
                    : 'All teams display the club logo. Coaches cannot upload a different logo.'}
                </p>
              </div>
            </label>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* ── Propagate to Teams ─────────────────────── */}
        {club?.teamIds?.length > 0 && (
          <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-6 space-y-4">
            <h2 className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>Apply Settings to Teams</h2>
            <p className="text-xs text-zinc-500">
              Push the current sport lock and logo permissions to all {club.teamIds.length} team{club.teamIds.length === 1 ? '' : 's'} under this club.
              Their sport and logo settings will be overwritten with the values currently saved above.
            </p>
            <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
              <p className="text-xs text-amber-700 font-semibold">
                This will update {club.teamIds.length} team{club.teamIds.length === 1 ? '' : 's'}.
                Their sport and logo settings will be overwritten.
              </p>
            </div>
            <button
              type="button"
              onClick={handlePropagate}
              disabled={propagating}
              className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all disabled:opacity-60"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              {propagating && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
              {propagating ? 'Applying…' : 'Apply current settings to all teams'}
            </button>
          </div>
        )}

        {/* ── Transfer ownership ─────────────────────────
            Owner-only escape hatch: hand off the club to another existing
            member. Sits ABOVE Danger Zone because it's the safer "I'm
            leaving" alternative to deletion — both teams + member data
            stay intact, only the captain changes. */}
        {isOwner && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 space-y-3">
            <div className="flex items-center gap-2">
              <ArrowsRightLeftIcon className="h-5 w-5 text-amber-700" />
              <h2 className="text-[11px] font-bold uppercase tracking-[1.4px] text-amber-800" style={{ fontFamily: HEAD_FONT }}>Transfer ownership</h2>
            </div>
            <p className="text-sm text-zinc-700">
              Hand off this club to another member. You'll be demoted to <span className="font-semibold text-[#0d1f14]">Club Admin</span> and keep access. Useful when you're stepping back but the club continues.
            </p>
            <button
              type="button"
              onClick={() => setTransferOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-bold uppercase text-amber-800 hover:bg-amber-100 transition-colors"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <ArrowsRightLeftIcon className="h-4 w-4" />
              Transfer ownership
            </button>
          </div>
        )}

        {/* ── Danger Zone ────────────────────────────── */}
        {isOwner && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
              <h2 className="text-[11px] font-bold uppercase tracking-[1.4px] text-red-700" style={{ fontFamily: HEAD_FONT }}>Danger Zone</h2>
            </div>

            <div>
              <p className="text-sm font-semibold text-[#0d1f14] mb-1">Delete this club</p>
              <p className="text-xs text-zinc-500 mb-3">
                This will permanently delete the club record. Your teams and their data will not be affected, but they will be unlinked from this club.
                Type <strong className="text-[#0d1f14]">{club.name}</strong> to confirm.
              </p>
              <div className="flex gap-3 flex-wrap">
                <input
                  type="text"
                  className={clsx(inputCls, 'max-w-xs border-red-300 focus:border-red-500 focus:ring-red-500/30')}
                  value={confirmDelete}
                  onChange={e => setConfirmDelete(e.target.value)}
                  placeholder={`Type "${club.name}" to confirm`}
                />
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || confirmDelete !== club.name}
                  className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold uppercase text-white hover:bg-red-500 disabled:opacity-40 transition-all active:scale-[0.97]"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  {deleting && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                  {deleting ? 'Deleting…' : 'Delete Club'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Owner-only handoff modal. Hidden when no transfer in flight. */}
      {isOwner && (
        <TransferOwnershipModal
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          club={club}
          members={members}
          currentOwnerUid={firebaseUser?.uid}
          onTransferred={() => {
            // After transfer the user is no longer owner — bounce them
            // to the club dashboard. useClub will re-subscribe with the
            // new role on the next render.
            navigate(`/clubs/${clubId}`);
          }}
        />
      )}
    </div>
  );
}
